import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import { unwrapApiData } from './unwrapApiData';
import type {
  ChangelogItem,
  ChangelogListQuery,
  ChangelogListResponse,
  CreateChangelogInput,
  UpdateChangelogInput,
} from '../../application/types';

const SYSTEM_CHANGELOG_BASE = '/system/owner/changelog';

export const changelogApi = createApi({
  reducerPath: 'changelogApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Changelog'],
  endpoints: build => ({
    getChangelogList: build.query<ChangelogListResponse, ChangelogListQuery | void>({
      query: params => ({
        url: SYSTEM_CHANGELOG_BASE,
        method: 'GET',
        params: params || undefined,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ChangelogListResponse>(response),
      providesTags: ['Changelog'],
    }),

    getChangelogById: build.query<ChangelogItem, number>({
      query: id => ({
        url: `${SYSTEM_CHANGELOG_BASE}/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ChangelogItem>(response),
      providesTags: (_result, _error, id) => [{ type: 'Changelog', id }],
    }),

    createChangelog: build.mutation<ChangelogItem, CreateChangelogInput>({
      query: body => ({
        url: SYSTEM_CHANGELOG_BASE,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ChangelogItem>(response),
      invalidatesTags: ['Changelog'],
    }),

    updateChangelog: build.mutation<
      ChangelogItem,
      { id: number; body: UpdateChangelogInput }
    >({
      query: ({ id, body }) => ({
        url: `${SYSTEM_CHANGELOG_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<ChangelogItem>(response),
      invalidatesTags: (_result, _error, { id }) => [
        'Changelog',
        { type: 'Changelog', id },
      ],
    }),
  }),
});

export const {
  useGetChangelogListQuery,
  useGetChangelogByIdQuery,
  useCreateChangelogMutation,
  useUpdateChangelogMutation,
} = changelogApi;
